import { z } from 'zod';

/**
 * Shared Zod schemas + helpers used by both ContactForm and ReportForm.
 * Error messages are pt-BR since the audience is Brazilian.
 */

export const BR_PHONE_REGEX = /^\+?\s*\(?\d{2}\)?\s*\d{4,5}-?\d{4}$/;

export const contactFormSchema = z
  .object({
    nome: z.string().trim().min(2, 'Informe seu nome'),
    email: z.string().trim().email('E-mail inválido'),
    telefone: z
      .string()
      .trim()
      .optional()
      .refine(
        (value) => !value || BR_PHONE_REGEX.test(value),
        'Telefone inválido. Exemplo: (11) 99999-9999'
      ),
    mensagem: z.string().trim().optional(),
    comunicacoes: z.boolean().default(false),
    privacidade: z
      .boolean()
      .refine((value) => value === true, 'Você precisa concordar para continuar'),
  })
  .strict();

export type ContactFormValues = z.infer<typeof contactFormSchema>;

export const REPORT_CATEGORIES = [
  'Conflito de interesse',
  'Desvios éticos e de conduta',
  'Falhas em controles internos',
  'Favorecimento ilícito',
  'Fraude',
  'Furto e roubo',
  'Retaliação',
  'Segurança da Informação',
  'Suborno',
  'Uso indevido de ativos',
  'Outros',
] as const;

export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const reportFormSchema = z
  .object({
    categoria: z.enum(REPORT_CATEGORIES, {
      errorMap: () => ({ message: 'Selecione uma categoria' }),
    }),
    outroAssunto: z.string().trim().optional(),
    denunciante: z.string().trim().optional(),
    descricao: z
      .string()
      .trim()
      .min(20, 'Descreva o ocorrido com pelo menos 20 caracteres'),
    privacidade: z
      .boolean()
      .refine((value) => value === true, 'Você precisa concordar para continuar'),
  })
  .superRefine((data, ctx) => {
    if (data.categoria === 'Outros' && (!data.outroAssunto || data.outroAssunto.length < 3)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['outroAssunto'],
        message: 'Descreva brevemente o assunto',
      });
    }
  });

export type ReportFormValues = z.infer<typeof reportFormSchema>;
